/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import './DataTableCommonTheme.scss';
import { Badge } from '@scuf/common';
import { ISavingsMeterWidgetProps } from './SavingsMeterProps';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import TruncateText from '../../shared/TruncatedText/TruncateText';
import { getBarColorClass } from './SavingMeterWidgetUtils';
import { getCategoryFromUtilityType } from '../Utils/Utils';
import {
  ConsumptionDeviation,
  ConsumptionHigh,
  ConsumptionTypeName
} from '../Utils/Constants';
import { ConversionType } from '../Utils/unitHandling/UnitModel';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';

const SavingsMeterWidget = (props: ISavingsMeterWidgetProps) => {
  const { meterData, columnHeaders, onSorting, hideChange } = props;
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(ConsumptionHigh);

  const getModifiedLabelForScopeOne = (value: string) => {
    if (value === 'Scope1') {
      return 'Scope 1';
    } else if (value === 'Scope2') {
      return 'Scope 2';
    } else {
      return value;
    }
  };

  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    if (field === ConsumptionTypeName) {
      const label = getCategoryFromUtilityType(value);
      return (
        <span className="table-cell-common emass-siteview-table-cell">
          <TruncateText
            text={
              props.toggleFeatureScopeOne
                ? getModifiedLabelForScopeOne(value)
                : label
            }
          />
        </span>
      );
    }
    if (field === ConsumptionHigh) {
      console.log('cellData in case of ConsumptionHigh', cellData);
      return (
        <div className="saving-value-cell">
          {rowData.SavingsPercentage && (
            <span className="style-span-percentage table-cell-common emass-siteview-table-cell">
              {rowData.SavingsPercentage}
            </span>
          )}
          <span className="table-cell-common emass-siteview-table-cell">
            <div className="savings-badge">
              {rowData.ConsumptionDeviation < 0 && !hideChange && (
                <Badge color="red" empty />
              )}{' '}
              {getConvertedValue(
                ConversionType.CARBON,
                props.emissionMeasurementUnit,
                value,
                rowData.unit
              ).Value +
                ' ' +
                cellData.rowData.unit}
            </div>
          </span>
        </div>
      );
    }
    if (field === ConsumptionDeviation) {
      const cssClass = getBarColorClass(rowData.ConsumptionDeviation)!;
      return (
        <div className="saving-value-cell">
          {rowData.SavingsPercentage && (
            <span className="style-span-percentage table-cell-common emass-siteview-table-cell">
              {rowData.SavingsPercentage}
            </span>
          )}
          <span className="table-cell-common emass-siteview-table-cell">
            {value === null || hideChange ? (
              '--'
            ) : (
              <span className={cssClass}>
                {' '}
                {getConvertedValue(
                  ConversionType.CARBON,
                  props.emissionMeasurementUnit,
                  Math.abs(value),
                  rowData.totalDeviationUnit
                ).Value +
                  ' ' +
                  rowData.totalDeviationUnit}{' '}
                ({Math.abs(rowData.deviationPercentage)}%)
              </span>
            )}
          </span>
        </div>
      );
    }
    return <></>;
  };
  return (
    <div className="emaas-saving-meter-table-container">
      <DataTable
        data={meterData}
        className="style-tableContent"
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
          if (sortField === ConsumptionHigh && onSorting) {
            onSorting(sortOrder as number);
          }
        }}
        sortField={sortFieldName}
        sortOrder={fieldSortOrder}
      >
        {columnHeaders.map((header) => (
          <DataTable.Column
            key={header.toString()}
            field={header.Field}
            header={header.HeaderValue}
            renderer={cellDataRenderer}
            align={header.TextAlignValue}
            sortable={header.sortable}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default SavingsMeterWidget;
